import React, { useEffect, useMemo, useRef, useState } from "react";
import { orientedDims } from "../byod-join/device-check";

/**
 * RecordingCoach
 * A React component that helps users capture **high‑quality** face + voice recordings
 * for facial recognition and speaker diarization workflows.
 *
 * Features
 * - Preflight checklist (lighting, background, posture, mic test)
 * - Device pickers (camera/microphone)
 * - Live video preview with safe‑framing overlay
 * - Face centering & size guidance (via FaceDetector API if available; fallback to heuristics)
 * - Lighting/brightness estimation from video frames
 * - Audio level meter (RMS, peak, clipping warnings)
 * - Background‑noise check (measures noise floor during a silent window)
 * - Guided script/teleprompter & countdown
 * - Test‑record + playback, then Full recording
 * - Records to WebM (VP8/VP9 + Opus) with sensible getUserMedia constraints
 *
 */

// Natural prompts instead of a scripted read-aloud. Two reasons:
// 1. Domain match: the print is compared against spontaneous DISCUSSION
//    speech later, and read-aloud prosody measurably differs from talking —
//    prints built from natural answers match classroom audio better.
// 2. People actually do it: a scripted tongue-twister gets rushed, flat,
//    embarrassed readings; questions get real voices.
// A few sentences per prompt yields the ~15+ seconds of net speech the
// server verifies (it asks for MORE audio — appended, not restarted — if a
// take is short). The topics still sweep a wide phonetic range (names,
// numbers, places, food, opinions) without feeling like a drill.
const PROMPTS = `No script — just answer a few of these out loud, in your normal speaking voice, a couple of sentences each:

•  Say your name, and what you're studying or working on this term.
•  How did you get here today? Describe the route like you're giving directions.
•  What did you eat most recently — and honestly, was it any good?
•  Explain something from a class to a friend who missed it.
•  Recommend a show, game, or song, and make your case for it.
•  What are your plans for the rest of the week?

Keep going until the meter fills — pauses and "um"s are completely fine.`;
const DEFAULT_SCRIPT = `Keep your face toward the camera and slowly turn your head left, right, up, and down while you talk. ` + PROMPTS;
// Voice only: nothing to say about the camera.
const VOICE_SCRIPT = `Hold the phone, or sit at the laptop, about as far from your mouth as in a normal call. ` + PROMPTS;

// Audio containers MediaRecorder can produce, best first (Opus in WebM on
// Chrome and Firefox, AAC in MP4 on Safari); the sign-up page probes the
// same list so the server is told the right extension.
const VOICE_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4;codecs=mp4a.40.2', 'audio/mp4'];
const VIDEO_MIME_TYPES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus'];
const supportedMime = (list) =>
    list.find((m) => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } }) || list[list.length - 1];

function RecordingCoach({
    // No camera: audio-only capture, no preview, no face guidance.
    voiceOnly = false,
    maxDurationSec = 60,
    minDurationSec = 10,
    onComplete,
    saveRecording,
    onTestClip,
    enrollStatus = { kind: "idle" },
    onDoneEnrolling,
    onSkipEnrolling,
    showScript = true,
    scriptText,
}) {
    const script = scriptText || (voiceOnly ? VOICE_SCRIPT : DEFAULT_SCRIPT);
    // Refs & state
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);

    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const scriptStartRef = useRef(null);

    // The elapsed-time loop is a requestAnimationFrame recursion whose only
    // exits used to be the test cap and the max-duration cap. A manual Stop
    // left it running, so the clock kept climbing to maxDurationSec and
    // overwrote actualTimeElapsed with the cap — a 50s take was saved, and
    // stamped into the WebM, as 60s. These two let a stop actually stop it.
    const tickActiveRef = useRef(false);
    const rafRef = useRef(null);
    // Read by stopRecording so the saved length is the tick's own last value
    // rather than whatever the last render happened to observe.
    const elapsedRef = useRef(0);

    const cancelTick = () => {
        tickActiveRef.current = false;
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    };

    const [devices, setDevices] = useState([]);
    const [micId, setMicId] = useState();
    const [camId, setCamId] = useState();

    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isRecordingStopped, setIsRecordingStopped] = useState(false)
    const [countdown, setCountdown] = useState(null);
    const [elapsed, setElapsed] = useState(0);

    const [avgLuma, setAvgLuma] = useState(0);
    const [faceOk, setFaceOk] = useState(false);
    const [faceBox, setFaceBox] = useState();

    const [rmsDb, setRmsDb] = useState(-Infinity);
    const [peakDb, setPeakDb] = useState(-Infinity);
    const [clipping, setClipping] = useState(false);
    const [noiseFloorDb, setNoiseFloorDb] = useState(null);

    const [hintsVideo, setHintsVideo] = useState([]);
    const [hintsAudio, setHintsAudio] = useState([]);
    const [previewError, setPreviewError] = useState("");
    const [videoBlobData, setVideoBlobData] = useState(null)
    const [actualTimeElapsed, setActualTimeElapsed] = useState(0)

    // Continuation mode: the server kept the previous audio and only needs
    // roughly the missing seconds of speech — don't demand a full-length take.
    const continuing = enrollStatus.kind === "continue"
    const effectiveMinSec = continuing
        ? Math.max(8, Math.ceil(enrollStatus.neededSeconds || 10) + 3)
        : minDurationSec

    const FaceDetectorApi = useMemo(() => (typeof window !== 'undefined' && (window).FaceDetector ? new (window).FaceDetector({ fastMode: true }) : null), []);

    // Enumerate devices
    useEffect(() => {
        navigator.mediaDevices?.enumerateDevices().then(setDevices).catch(() => { });
    }, []);

    // Start preview with selected devices
    const startPreview = async () => {
        stopEverything();
        setPreviewError("");
        try {
            await startPreviewInner();
        } catch (ex) {
            // A permission denial used to be an unhandled rejection — the
            // Start Preview button just looked dead.
            const what = voiceOnly ? "microphone" : "camera/microphone";
            setPreviewError(
                ex && ex.name === "NotAllowedError"
                    ? `${what[0].toUpperCase()}${what.slice(1)} access was blocked — allow it in your browser settings and try again.`
                    : `Couldn't open the ${what} (` + ((ex && ex.name) || "error") + "). Close other apps using it and try again.",
            );
        }
    };

    const startPreviewInner = async () => {
        const constraints = {
            video: voiceOnly ? false : {
                deviceId: camId ? { exact: camId } : undefined,
                // Oriented to how the phone is held, so an upright phone
                // records portrait instead of a cropped landscape box.
                ...orientedDims(1280, 720),
                frameRate: { ideal: 30, max: 30 },
                facingMode: 'user',
            },
            audio: {
                deviceId: micId ? { exact: micId } : undefined,
                channelCount: 1,
                sampleRate: 16000,
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: false,
            },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        mediaStreamRef.current = stream;

        if (!voiceOnly && videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play();
        }

        // audio chain
        const ctx = new AudioContext();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        sourceRef.current = source;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        analyserRef.current = analyser;
        source.connect(analyser);

        // warm up noise floor during 2s silence window
        setNoiseFloorDb(null);
        const noiseWindowMs = 1500;
        const start = performance.now();
        const data = new Float32Array(analyser.fftSize);

        const sampleNoise = () => {
            if (!analyserRef.current) return;
            analyserRef.current.getFloatTimeDomainData(data);
            const rms = Math.sqrt(data.reduce((s, v) => s + v * v, 0) / data.length) || 1e-8;
            const db = 20 * Math.log10(rms);
            if (performance.now() - start > noiseWindowMs) {
                setNoiseFloorDb(db);
            } else {
                requestAnimationFrame(sampleNoise);
            }
        };
        requestAnimationFrame(sampleNoise);

        setIsPreviewing(true);
        startMeters();
        if (!voiceOnly) startVisionLoop();
    };

    const stopEverythingRef = useRef(null);
    const stopEverything = () => {
        cancelTick();
        elapsedRef.current = 0;
        stopMeters();
        stopVisionLoop();

        mediaRecorderRef.current?.stop();
        mediaRecorderRef.current = null;

        mediaStreamRef.current?.getTracks().forEach(t => t.stop());
        mediaStreamRef.current = null;

        audioCtxRef.current?.close();
        audioCtxRef.current = null;

        setIsPreviewing(false);
        setIsRecording(false);
        setIsTesting(false);
        setIsRecordingStopped(false)
        setVideoBlobData(null)
        setCountdown(null);
        setElapsed(0);
    };
    stopEverythingRef.current = stopEverything;

    // Audio meters. Refs, not per-render `let`s — the meter loop calls
    // setState every frame, so a re-render was guaranteed between start and
    // stop, and stop cancelled a stale (usually 0) id: both loops then ran
    // (FaceDetector per frame included) until the tab reloaded. Same fix the
    // recorder-tick loop already has (rafRef/tickActiveRef above).
    const meterRAF = useRef(null);
    const meterActive = useRef(false);
    const startMeters = () => {
        const analyser = analyserRef.current;
        if (!analyser) return;
        const buf = new Float32Array(analyser.fftSize);
        meterActive.current = true;
        const loop = () => {
            if (!meterActive.current) return;
            analyser.getFloatTimeDomainData(buf);
            // RMS
            const rms = Math.sqrt(buf.reduce((s, v) => s + v * v, 0) / buf.length) || 1e-8;
            const peak = buf.reduce((p, v) => Math.max(p, Math.abs(v)), 0) || 1e-8;
            const rmsDbNew = 20 * Math.log10(rms);
            const peakDbNew = 20 * Math.log10(peak);
            setRmsDb(rmsDbNew);
            setPeakDb(peakDbNew);
            setClipping(peak > 0.98); // near full‑scale
            meterRAF.current = requestAnimationFrame(loop);
        };
        meterRAF.current = requestAnimationFrame(loop);
    };
    const stopMeters = () => {
        meterActive.current = false;
        if (meterRAF.current !== null) {
            cancelAnimationFrame(meterRAF.current);
            meterRAF.current = null;
        }
    };

    // Vision loop (brightness + face guidance)
    const visionRAF = useRef(null);
    const visionActive = useRef(false);
    const startVisionLoop = () => {
        const v = videoRef.current;
        const c = canvasRef.current;
        if (!v || !c) return;
        const ctx = c.getContext('2d');
        if (!ctx) return;

        visionActive.current = true;
        const loop = async () => {
            if (!visionActive.current) return;
            if (!v.videoWidth || !v.videoHeight) {
                visionRAF.current = requestAnimationFrame(loop);
                return;
            }
            c.width = v.videoWidth;
            c.height = v.videoHeight;
            ctx.drawImage(v, 0, 0, c.width, c.height);

            // average luma (very cheap): convert to 1px via drawImage scaling
            const thumbW = 32, thumbH = 18;
            const tmp = document.createElement('canvas');
            tmp.width = thumbW; tmp.height = thumbH;
            const tctx = tmp.getContext('2d');
            tctx.drawImage(c, 0, 0, thumbW, thumbH);
            const img = tctx.getImageData(0, 0, thumbW, thumbH).data;
            let sum = 0;
            for (let i = 0; i < img.length; i += 4) {
                const r = img[i], g = img[i + 1], b = img[i + 2];
                // Rec. 601 luma approximation
                sum += 0.299 * r + 0.587 * g + 0.114 * b;
            }
            const luma = sum / (img.length / 4);
            setAvgLuma(luma);

            // Face detection if available
            let fOk = false;
            let fBox = undefined;
            try {
                if (FaceDetectorApi) {
                    const faces = await FaceDetectorApi.detect(v);
                    if (faces && faces[0] && faces[0].boundingBox) {
                        const bb = faces[0].boundingBox;
                        fBox = { x: bb.x, y: bb.y, width: bb.width, height: bb.height };
                        // Heuristic: face box covers ~15–60% of height and centered
                        const sizePct = bb.height / v.videoHeight;
                        const centerX = bb.x + bb.width / 2;
                        const centerY = bb.y + bb.height / 2;
                        const cxOk = Math.abs(centerX - v.videoWidth / 2) < v.videoWidth * 0.15;
                        const cyOk = Math.abs(centerY - v.videoHeight / 2) < v.videoHeight * 0.15;
                        fOk = sizePct > 0.15 && sizePct < 0.6 && cxOk && cyOk;
                    }
                }
            } catch { }
            setFaceBox(fBox);
            setFaceOk(fOk);

            // Hints
            const vHints = [];
            if (luma < 80) vHints.push('Increase lighting in front of your face.');
            if (luma > 220) vHints.push('Lighting is too bright; reduce glare or move back.');
            if (FaceDetectorApi && !fOk) vHints.push('Center your face and keep eyes level; fill ~1/3 of frame.');
            setHintsVideo(vHints);

            if (!visionActive.current) return;
            visionRAF.current = requestAnimationFrame(loop);
        };
        visionRAF.current = requestAnimationFrame(loop);
    };
    const stopVisionLoop = () => {
        visionActive.current = false;
        if (visionRAF.current !== null) {
            cancelAnimationFrame(visionRAF.current);
            visionRAF.current = null;
        }
    };

    // Start a recording (test or full)
    const beginRecording = async (mode) => {
        if (!mediaStreamRef.current) return;
        cancelTick();
        elapsedRef.current = 0;
        recordedChunksRef.current = [];
        const mime = supportedMime(voiceOnly ? VOICE_MIME_TYPES : VIDEO_MIME_TYPES);

        const rec = new MediaRecorder(mediaStreamRef.current, voiceOnly
            ? { mimeType: mime, audioBitsPerSecond: 128_000 }
            : { mimeType: mime, videoBitsPerSecond: 3_000_000, audioBitsPerSecond: 128_000 });
        mediaRecorderRef.current = rec;

        rec.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
        };
        rec.onstop = () => {
            const blob = new Blob(recordedChunksRef.current, { type: rec.mimeType });
            const diagnostics = {
                video: { averageLuma: avgLuma, faceOk, faceBox, hints: hintsVideo },
                audio: { noiseFloorDb: noiseFloorDb ?? -Infinity, rmsDb, clipping, hints: hintsAudio },
                device: { cameraId: camId, micId: micId },
            };
            if (mode === 'test') onTestClip?.(blob, diagnostics);
            else {
                setVideoBlobData(blob) //onComplete?.(blob, diagnostics);
            }
        };

        // countdown
        await runCountdown(3);

        if (mode === 'full') setIsRecording(true); else setIsTesting(true);
        scriptStartRef.current = performance.now();
        const startedAt = Date.now();
        rec.start(250);

        tickActiveRef.current = true;
        const tick = () => {
            // A manual Stop clears the flag; without this the loop outlived the
            // recorder and kept reporting time that was never recorded.
            if (!tickActiveRef.current) return;

            const sec = Math.floor((Date.now() - startedAt) / 1000);
            elapsedRef.current = sec;
            setElapsed(sec);

            // Stop logic
            if (mode === 'test' && sec >= 5) {
                cancelTick();
                if (rec.state !== 'inactive') rec.stop();
                setIsTesting(false);
                return;
            }
            if (mode === 'full' && sec >= maxDurationSec) {
                cancelTick();
                if (rec.state !== 'inactive') rec.stop();
                setIsRecording(false);
                // Hitting the cap must still surface the Save button —
                // previously only a manual Stop did.
                setIsRecordingStopped(true);
                setActualTimeElapsed(sec);
                return;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    };

    const stopRecording = () => {
        // Stop the clock before the recorder: the displayed time must freeze
        // where the take actually ended, and that value is what gets saved.
        cancelTick();
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        if (isRecording) {
            setIsRecordingStopped(true)
        }
        setIsRecording(false);
        setIsTesting(false);
        setElapsed(elapsedRef.current);
        setActualTimeElapsed(elapsedRef.current)

    };

    const startSaveRecording = () => {
        if (videoBlobData !== null && actualTimeElapsed >= effectiveMinSec) {
            saveRecording(videoBlobData, actualTimeElapsed)
        }
    }
    const runCountdown = (n) =>
        new Promise((resolve) => {
            let val = n;
            setCountdown(val);
            const id = setInterval(() => {
                val -= 1;
                if (val <= 0) {
                    clearInterval(id);
                    setCountdown(null);
                    resolve();
                } else setCountdown(val);
            }, 1000);
        });

    // Compute audio hints
    useEffect(() => {
        const hints = [];
        if (noiseFloorDb !== null) {
            if (noiseFloorDb > -40) hints.push('Background noise is high; move to a quieter room.');
            if (noiseFloorDb < -70) hints.push('Room is very quiet (good).');
        }
        if (rmsDb > -18) hints.push('Reduce mic gain or move mic farther (too loud).');
        if (rmsDb < -35) hints.push('Speak a bit louder or move closer to mic.');
        if (clipping) hints.push('Audio clipping detected; lower input level.');
        setHintsAudio(hints);
    }, [noiseFloorDb, rmsDb, clipping]);

    // Cleanup on unmount
    useEffect(() => () => stopEverythingRef.current(), []);

    // Helpers
    const cameras = devices.filter(d => d.kind === 'videoinput');
    const mics = devices.filter(d => d.kind === 'audioinput');

    return (
        <div className="mx-auto max-w-5xl p-6 space-y-6 overflow-y-auto h-screen">
            <header className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">Recording Guide</h1>
                <div className="text-sm text-tiilt-muted">{voiceOnly ? "Voice only — no camera" : "Optimized for face+voice capture"}</div>
            </header>

            {/* Preflight checklist */}
            <section className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border p-4 shadow-sm">
                    <h2 className="mb-2 font-medium">Preflight</h2>
                    <ul className="space-y-2 text-sm">
                        {voiceOnly ? null : <li>• Ensure your face is within the rectangular box.</li>}
                        {voiceOnly ? null : <li>• Face a window or soft light; avoid strong backlight.</li>}
                        {voiceOnly ? null : <li>• Plain background; remove hats/hoods; keep glasses clean.</li>}
                        <li>• Quiet room; turn off fans/AC where possible.</li>
                        <li>• Speak at normal pace; avoid sudden shouting/laughter.</li>
                        {voiceOnly ? <li>• Use the built-in microphone or a headset; keep it a hand's width from your mouth.</li> : null}
                    </ul>
                </div>


                {voiceOnly ? (
                    <div className="rounded-2xl border p-4 shadow-sm text-sm text-tiilt-muted">
                        <div className="font-medium text-tiilt-ink">What the sample is for</div>
                        <ul className="mt-1 list-inside list-disc">
                            <li>BLINC compares it with your voice in class to tell who said what.</li>
                            <li>Natural talking matches better than reading, so answer the prompts rather than reciting.</li>
                            <li>No face sample is taken: you will not be recognized on video, only in audio.</li>
                        </ul>
                    </div>
                ) : (
                    <div className="rounded-2xl border p-4 shadow-sm text-sm text-tiilt-muted">
                        <div className="font-medium text-tiilt-ink">Capture Tips (FR + Diarization)</div>
                        <ul className="mt-1 list-inside list-disc">
                            <li>Keep head upright, look at camera; avoid extreme angles & occlusions.</li>
                            <li>Maintain neutral to mild expressions for FR enrollment; smile only if consistent with target set.</li>
                            <li>You could turn your head to the right, left, up and down while speaking; to ensure all head orientations are captured.</li>
                        </ul>
                    </div>
                )}

                <div className="rounded-2xl border p-4 shadow-sm">
                    <h2 className="mb-2 font-medium">Devices</h2>
                    <div className="flex gap-3">
                        {voiceOnly ? null : (
                        <label className="flex-1 text-sm">Camera
                            <select className="mt-1 w-full rounded-lg border p-2" value={camId} onChange={e => setCamId(e.target.value)}>
                                <option value="">Default</option>
                                {cameras.map(d => (<option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${d.deviceId.slice(-4)}`}</option>))}
                            </select>
                        </label>
                        )}
                        <label className="flex-1 text-sm">Microphone
                            <select className="mt-1 w-full rounded-lg border p-2" value={micId} onChange={e => setMicId(e.target.value)}>
                                <option value="">Default</option>
                                {mics.map(d => (<option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(-4)}`}</option>))}
                            </select>
                        </label>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button className="rounded-xl bg-tiilt px-4 py-2 text-white shadow" onClick={startPreview} disabled={isPreviewing}>Start Preview</button>
                        <button className="rounded-xl border px-4 py-2" onClick={stopEverything}>Stop</button>
                    </div>
                </div>

                <div className="rounded-2xl border p-4 shadow-sm">
                    <h3 className="mb-2 font-medium">Mic Levels</h3>
                    <LevelMeter rmsDb={rmsDb} peakDb={peakDb} clipping={clipping} />
                    <div className="mt-1 text-xs text-tiilt-muted">Target RMS: −35 to −18 dBFS (green zone)</div>
                </div>
            </section>

            {/* Live Preview (voice only: just the listening status, no picture) */}
            <section className="grid gap-4 md:grid-cols-3">
                {voiceOnly ? (
                <div className="md:col-span-2 rounded-2xl border p-4 shadow-sm">
                    <div className="flex items-center gap-3 rounded-xl bg-tiilt-ground p-4 text-sm text-tiilt-ink">
                        <span className={`h-3 w-3 flex-none rounded-full ${isPreviewing ? 'bg-emerald-500' : 'bg-tiilt-line'}`} />
                        {isPreviewing
                            ? (isRecording ? "Recording your voice…" : "Listening — the meter above shows your level. Press Start Recording when you are ready.")
                            : "Press Start Preview to open the microphone."}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                        <Badge ok={noiseFloorDb !== null && noiseFloorDb < -40} label={`Noise floor: ${noiseFloorDb === null ? '…' : noiseFloorDb.toFixed(1) + ' dB'}`} />
                    </div>
                </div>
                ) : (
                <div className="md:col-span-2 rounded-2xl border p-3 shadow-sm">
                    <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
                        <video ref={videoRef} className="h-full w-full object-cover" playsInline muted />
                        {/* Safe frame overlay */}
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                            <div className="h-3/4 w-1/2 rounded-3xl border-4 border-white/70"></div>
                        </div>
                        {/* Detected face box */}
                        {faceBox && (
                            <div
                                className="pointer-events-none absolute border-2 border-emerald-400"
                                style={{ left: faceBox.x, top: faceBox.y, width: faceBox.width, height: faceBox.height }}
                            />
                        )}
                    </div>
                    <canvas ref={canvasRef} className="hidden" />
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                        <Badge ok={avgLuma >= 100 && avgLuma <= 200} label={`Brightness: ${avgLuma.toFixed(0)}`} />
                        <Badge ok={noiseFloorDb !== null && noiseFloorDb < -40} label={`Noise floor: ${noiseFloorDb === null ? '…' : noiseFloorDb.toFixed(1) + ' dB'}`} />
                        {FaceDetectorApi && <Badge ok={faceOk} label={`Face centered: ${faceOk ? 'OK' : 'Adjust'}`} />}
                    </div>
                </div>
                )}

                {/* script */}
                <div className="flex flex-col gap-4">

                    {showScript && (
                        <div className="rounded-2xl border p-4 shadow-sm">
                            <h3 className="mb-2 font-medium">Things to talk about</h3>
                            <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-xl bg-tiilt-ground p-3 text-sm leading-relaxed">
                                {script}
                            </div>
                        </div>
                    )}

                    {/* Controls */}
                    <div className="rounded-2xl border p-4 shadow-sm">
                        <div className="flex flex-wrap items-center gap-3">
                            <button className="rounded-xl border px-4 py-2" onClick={() => beginRecording('test')} disabled={!isPreviewing || isRecording || isTesting}>Test 5s</button>
                            <button className="rounded-xl bg-emerald-600 px-4 py-2 text-white shadow disabled:opacity-50" onClick={() => beginRecording('full')} disabled={!isPreviewing || isRecording || isTesting}>
                                {isRecording ? 'Recording…' : 'Start Recording'}
                            </button>
                            {(isRecording || isTesting) && (
                                <button className="rounded-xl bg-red-600 px-4 py-2 text-white" onClick={stopRecording}>Stop</button>
                            )}

                            {isRecordingStopped && actualTimeElapsed >= effectiveMinSec && enrollStatus.kind !== "success" && (
                                <button
                                    className="rounded-xl bg-emerald-600 px-4 py-2 text-white shadow disabled:opacity-50"
                                    onClick={startSaveRecording}
                                    disabled={enrollStatus.kind === "saving"}
                                >
                                    {enrollStatus.kind === "quality_failed" || enrollStatus.kind === "error" ? "Save again" : "Save"}
                                </button>
                            )}
                            {countdown !== null && <span className="ml-2 text-lg font-semibold">{countdown}</span>}
                            <span className="ml-auto text-sm text-tiilt-muted">{elapsed}s / {maxDurationSec}s</span>
                        </div>
                        {isRecordingStopped && actualTimeElapsed < effectiveMinSec ? (
                            <div className="mt-2 text-xs font-semibold text-amber-600 dark:text-amber-400">
                                That was only {actualTimeElapsed}s — please record again and
                                record at least {effectiveMinSec}s.
                            </div>
                        ) : (
                            <div className="mt-2 text-xs text-tiilt-muted">
                                Talk through a few prompts — at least {effectiveMinSec}s. The
                                recording is checked automatically and you may be asked to
                                record a bit more.
                            </div>
                        )}

                        {/* Inline enrollment verification status (replaces the
                            success / quality popups). */}
                        {enrollStatus.kind === "saving" ? (
                            <div className="mt-3 flex items-center gap-3 rounded-xl border border-tiilt-line bg-tiilt-ground/60 p-3 text-sm text-tiilt-ink">
                                <span className="h-4 w-4 flex-none animate-spin rounded-full border-2 border-tiilt border-t-transparent" />
                                Verifying and saving your voice{voiceOnly ? "" : " and face"} — this
                                takes about half a minute. Keep this page open.
                            </div>
                        ) : null}
                        {enrollStatus.kind === "success" ? (
                            <div className="mt-3 rounded-xl border border-tiilt-teal/40 bg-tiilt-teal/15 p-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-tiilt-teal-text">
                                    <span aria-hidden="true">✓</span> Enrollment complete
                                </div>
                                <div className="mt-1 text-sm text-tiilt-ink">{enrollStatus.message}</div>
                                <button
                                    className="mt-3 rounded-xl bg-tiilt px-4 py-2 text-sm font-semibold text-white shadow hover:bg-tiilt-deep"
                                    onClick={onDoneEnrolling}
                                >
                                    Done
                                </button>
                            </div>
                        ) : null}
                        {enrollStatus.kind === "continue" ? (
                            <div className="mt-3 rounded-xl border border-tiilt-teal/40 bg-tiilt-teal/10 p-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-tiilt-teal-text">
                                    <span aria-hidden="true">＋</span> Almost there — keep going
                                </div>
                                <div className="mt-1 text-sm leading-relaxed text-tiilt-ink">
                                    {enrollStatus.message} Record about{" "}
                                    {Math.ceil(enrollStatus.neededSeconds || 10)}s more of
                                    speaking and press Save — it will be added to what you
                                    already recorded, not replace it.
                                </div>
                            </div>
                        ) : null}
                        {enrollStatus.kind === "quality_failed" ? (
                            <div className="mt-3 rounded-xl border border-tiilt-orange/40 bg-tiilt-orange/15 p-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-tiilt-orange-text">
                                    <span aria-hidden="true">↻</span> Please record again
                                </div>
                                <div className="mt-1 text-sm leading-relaxed text-tiilt-ink">{enrollStatus.message}</div>
                            </div>
                        ) : null}
                        {enrollStatus.kind === "error" ? (
                            <div className="mt-3 rounded-xl border border-tiilt-danger/40 bg-tiilt-danger-soft p-3">
                                <div className="flex items-center gap-2 text-sm font-semibold text-tiilt-danger">
                                    <span aria-hidden="true">!</span> Something went wrong
                                </div>
                                <div className="mt-1 text-sm leading-relaxed text-tiilt-ink">{enrollStatus.message}</div>
                            </div>
                        ) : null}

                        {/* Enrolling is optional and can be finished any time: the
                            account is committed before this page loads, and joining a
                            session never checks for a voice print. Without a way out,
                            the only exit was closing the tab — which works, but leaves
                            people unsure whether they have an account at all. Hidden
                            once enrollment succeeds, since Done covers that. */}
                        {enrollStatus.kind !== "success" && enrollStatus.kind !== "saving" && !isRecording && !isTesting ? (
                            <div className="mt-3 border-t border-tiilt-line pt-3">
                                <div className="text-xs text-tiilt-muted">
                                    Your account is already created. Enrolling your voice{voiceOnly ? "" : " and face"} helps
                                    the system tell who said what — you can do it now or come
                                    back any time.
                                </div>
                                <button
                                    type="button"
                                    className="mt-2 cursor-pointer rounded-xl border border-tiilt-line px-4 py-2 text-sm font-semibold text-tiilt-ink transition hover:bg-tiilt-soft"
                                    onClick={() => {
                                        stopEverything();
                                        if (onSkipEnrolling) onSkipEnrolling();
                                    }}
                                >
                                    Skip for now
                                </button>
                            </div>
                        ) : null}
                    </div>
                </div>
            </section>

            {/* Controls */}
            <section className="rounded-2xl border p-4 shadow-sm">
                 {previewError && (
                        <div className="mt-2 rounded-xl bg-red-50 p-3 text-sm text-red-900">
                            {previewError}
                        </div>
                    )}
                 {(hintsVideo.length > 0 || hintsAudio.length > 0) && (
                        <div className="mt-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                            <div className="font-medium">Suggestions</div>
                            <ul className="list-inside list-disc">
                                {[...hintsVideo, ...hintsAudio].map((h, i) => <li key={i}>{h}</li>)}
                            </ul>
                        </div>
                    )}
            </section>

        </div>
    );
}

function Badge({ ok, label }) {
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${ok ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'}`}>
            <span className={`h-2 w-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
            {label}
        </span>
    );
}

function LevelMeter({ rmsDb, peakDb, clipping }) {
    // Map −60..0 dBFS to 0..100
    const norm = (db) => Math.max(0, Math.min(100, 100 * (1 + db / 60)));
    const rmsPct = norm(rmsDb);
    const peakPct = norm(peakDb);
    return (
        <div>
            <div className="relative h-3 w-full rounded bg-tiilt-line/60">
                <div className="absolute left-0 top-0 h-3 rounded bg-green-500" style={{ width: `${rmsPct}%` }} />
                <div className="absolute left-0 top-0 h-3 rounded bg-black/30" style={{ width: `${peakPct}%` }} />
            </div>
            <div className="mt-1 flex items-center justify-between text-xs text-tiilt-muted">
                <span>RMS {Number.isFinite(rmsDb) ? rmsDb.toFixed(1) : '…'} dBFS</span>
                <span>Peak {Number.isFinite(peakDb) ? peakDb.toFixed(1) : '…'} dBFS {clipping && <strong className="text-red-600 dark:text-red-400">(CLIP)</strong>}</span>
            </div>
        </div>
    );
}

export { RecordingCoach }